import { useMutation, useQuery } from '@apollo/client/react';
import * as Sentry from '@sentry/react';
import { useCallback, useMemo, useRef, useState } from 'react';

import {
  BASKET_ID_KEY,
  INSURANCE_WARNING_SEEN_KEY,
} from '@constants/storageKeys';
import { useSimsStore } from '@features/Mobile/store';
import { BasketGuardQuery } from '@generated/gql/graphql';
import { useHostedAttribution } from '@providers/state/HostedAttributionProvider';
import { useSetBasketId } from '@store/basket/selectors';
import { useTrackingContext } from '@tracking/Provider/Context';
import { useMixpanelFlagsContextSync } from '@tracking/hooks';
import { addBasketIdToRemoteAppointment } from '@utils/queries/appointment';
import { basketGuardQuery } from '@utils/queries/basketGuard';
import { requoteBasket as requoteBasketQuery } from '@utils/queries/getBasket';

import {
  type BasketContextState,
  type BasketId,
  type BasketProviderProps,
} from '../types';
import { BasketContext } from './Context';

const getBasketId = () => {
  const basketId = window.sessionStorage.getItem(BASKET_ID_KEY) ?? '';
  Sentry.setTag('basket_id', basketId);
  return basketId;
};

const BasketProvider = ({ children }: BasketProviderProps) => {
  const { clearSims } = useSimsStore();
  const { clearAttribution } = useHostedAttribution();
  const { resetTrackingContext } = useTrackingContext();
  const [basketId, _setBasketId] = useState<BasketId>(getBasketId);
  const setStoredBasketId = useSetBasketId();

  // Bucket Mixpanel feature flags by basket, so everyone sharing a basket
  // (remote appointments) is shown the same variant.
  useMixpanelFlagsContextSync(basketId);

  // we're using this to mark whether a basket *and* it's supplementary
  // tasks are in progress or not. It is sued in:
  // * `src/hooks/useClearSubmittedBasket.ts`
  // * `src/hooks/submitOrder/useOrderSubmitter.ts`
  //
  // Because we are submitting renewal dates in the latter, and since the
  // former relies on the `submittedAt` property on the basket
  // to determine whether to redirect a completed basket back to the start
  // of the journey, it will do so prematurely due to hook re-calcualtions
  // prior to navigating to the ThankYou page. Thus, we're making
  // `applicationSubmitting` as `false` only on the ThankYou page itself.
  const [applicationSubmitting, setApplicationSubmitting] =
    useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: basketGuardData, loading: basketGuardLoading } = useQuery(
    basketGuardQuery,
    {
      variables: { id: basketId },
      skip: !basketId,
    }
  );

  const [
    requoteBasket,
    { loading: requoteBasketLoading, error: requoteBasketError },
  ] = useMutation(requoteBasketQuery);

  const [addBasketIdToAppointment] = useMutation(
    addBasketIdToRemoteAppointment
  );

  const setBasketId = useCallback(
    (id: string) => {
      Sentry.setTag('basket_id', id);
      window.sessionStorage.setItem(BASKET_ID_KEY, id);
      _setBasketId(id);
      setStoredBasketId(id);
    },
    [setStoredBasketId]
  );

  const handleBasketCreated = useCallback(
    (id: string, apptId?: string) => {
      setBasketId(id);
      window.localStorage.setItem(BASKET_ID_KEY, id);

      if (apptId) {
        addBasketIdToAppointment({
          variables: {
            id: apptId,
            basketId: id,
          },
        });
      }
    },
    [setBasketId, addBasketIdToAppointment]
  );

  const handleApplicationSubmitted = useCallback(() => {
    window.localStorage.removeItem(BASKET_ID_KEY);
  }, []);

  const handleStartAgain = useCallback(
    ({ removeHostedAttribution = true } = {}) => {
      _setBasketId('');
      setStoredBasketId(null);
      clearSims();
      resetTrackingContext();
      window.sessionStorage.removeItem(BASKET_ID_KEY);
      if (removeHostedAttribution) {
        clearAttribution();
      }
      window.localStorage.removeItem(BASKET_ID_KEY);
      window.sessionStorage.removeItem(INSURANCE_WARNING_SEEN_KEY);
    },
    [clearAttribution, clearSims, resetTrackingContext, setStoredBasketId]
  );

  const handleRequoteBasket = useCallback(
    (basketId: string) => {
      return requoteBasket({ variables: { basketId } });
    },
    [requoteBasket]
  );

  const stateValues: BasketContextState = useMemo(
    () => ({
      basketId,
      basketGuardData: basketGuardData as BasketGuardQuery,
      basketGuardLoading,
      containerRef,
      handleBasketCreated,
      handleApplicationSubmitted,
      handleStartAgain,
      setBasketId,
      applicationSubmitting,
      setApplicationSubmitting,
      requote: {
        call: handleRequoteBasket,
        loading: requoteBasketLoading,
        error: requoteBasketError,
      },
    }),
    [
      basketId,
      basketGuardData,
      basketGuardLoading,
      handleBasketCreated,
      handleApplicationSubmitted,
      handleStartAgain,
      setBasketId,
      applicationSubmitting,
      handleRequoteBasket,
      requoteBasketLoading,
      requoteBasketError,
    ]
  );

  return <BasketContext value={stateValues}>{children}</BasketContext>;
};

export { BasketProvider };
